import React from 'react';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';

/**
 * Reusable skeleton card matching NyomiriCard dimensions (176px, 2:3 aspect ratio).
 */
export function NyomiriSkeletonCard({ className = '' }: { className?: string }) {
  return (
    <Box
      className={`nyomiri-card-skeleton nyomiri-discover-skeleton-card ${className}`}
      sx={{
        width: 176,
        minWidth: 176,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        scrollSnapAlign: 'start',
      }}
    >
      <Skeleton
        variant="rounded"
        animation="wave"
        sx={{
          width: '100%',
          aspectRatio: '2/3',
          borderRadius: '8px',
          bgcolor: 'var(--bg-card, #0c0c12)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
        }}
      />
      <Skeleton
        variant="text"
        animation="wave"
        sx={{
          mt: 1,
          width: '85%',
          fontSize: 13,
          bgcolor: 'var(--bg-card, #0c0c12)',
        }}
      />
      <Skeleton
        variant="text"
        animation="wave"
        sx={{
          mt: 0.35,
          width: '50%',
          fontSize: 12,
          bgcolor: 'var(--bg-card, #0c0c12)',
        }}
      />
    </Box>
  );
}

/**
 * Horizontal rail skeleton loader matching NyomiriRail dimensions.
 */
export function NyomiriSkeletonRail({ title = 'Cargando carrusel' }: { title?: string }) {
  return (
    <Box component="section" aria-label={title} aria-busy="true" sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.75 }}>
        <Skeleton
          variant="text"
          animation="wave"
          sx={{ width: 140, height: 28, fontSize: 20, bgcolor: 'var(--bg-card, rgba(255,255,255,0.05))' }}
        />
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Skeleton
            variant="rounded"
            animation="wave"
            sx={{ width: 32, height: 32, borderRadius: '6px', bgcolor: 'var(--bg-card, rgba(255,255,255,0.05))' }}
          />
          <Skeleton
            variant="rounded"
            animation="wave"
            sx={{ width: 32, height: 32, borderRadius: '6px', bgcolor: 'var(--bg-card, rgba(255,255,255,0.05))' }}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: { xs: 1.5, sm: 2 },
          overflowX: 'hidden',
          py: 0.5,
        }}
      >
        {Array.from({ length: 7 }).map((_, i) => (
          <NyomiriSkeletonCard key={i} />
        ))}
      </Box>
    </Box>
  );
}

/**
 * Hero banner skeleton matching NyomiriHero layout.
 * Uses custom container to prevent altering aspect ratio tests of first .MuiSkeleton-rounded.
 */
export function NyomiriSkeletonHero() {
  return (
    <Box
      className="nyomiri-hero nyomiri-hero-skeleton nyomiri-hero-v2"
      aria-busy="true"
      sx={{
        position: 'relative',
        height: 'clamp(380px, 56vh, 560px)',
        width: '100%',
        flexShrink: 0,
        bgcolor: '#050508',
        borderRadius: 0,
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'flex-end',
        p: { xs: 3, md: 6 },
        pb: { xs: 4, md: 7 },
      }}
    >
      <Box sx={{ maxWidth: 640, width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Skeleton
          variant="text"
          animation="wave"
          sx={{ width: 120, height: 16, bgcolor: 'rgba(255, 255, 255, 0.08)' }}
        />
        <Skeleton
          variant="text"
          animation="wave"
          sx={{ width: '80%', height: 52, bgcolor: 'rgba(255, 255, 255, 0.08)' }}
        />
        <Skeleton
          variant="text"
          animation="wave"
          sx={{ width: '95%', height: 20, bgcolor: 'rgba(255, 255, 255, 0.08)' }}
        />
        <Skeleton
          variant="text"
          animation="wave"
          sx={{ width: '60%', height: 20, bgcolor: 'rgba(255, 255, 255, 0.08)' }}
        />
        <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
          <Skeleton
            variant="rectangular"
            animation="wave"
            sx={{ width: 140, height: 40, borderRadius: '10px', bgcolor: 'rgba(255, 255, 255, 0.1)' }}
          />
          <Skeleton
            variant="rectangular"
            animation="wave"
            sx={{ width: 110, height: 40, borderRadius: '10px', bgcolor: 'rgba(255, 255, 255, 0.08)' }}
          />
        </Box>
      </Box>
    </Box>
  );
}

/**
 * Catalog grid skeleton loader matching responsive grid columns.
 */
export function NyomiriSkeletonGrid({ count = 12 }: { count?: number }) {
  return (
    <Box
      className="nyomiri-discover-grid"
      aria-busy="true"
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, minmax(0, 1fr))',
          sm: 'repeat(3, minmax(0, 1fr))',
          md: 'repeat(4, minmax(0, 1fr))',
          lg: 'repeat(6, minmax(0, 1fr))',
        },
        gap: { xs: 1.5, sm: 2 },
        width: '100%',
        my: 2,
      }}
    >
      {Array.from({ length: count }).map((_, i) => (
        <NyomiriSkeletonCard key={i} />
      ))}
    </Box>
  );
}

/**
 * Structured detail view skeleton loader matching YomiriDetailView dimensions.
 */
export function NyomiriSkeletonDetail() {
  return (
    <article className="nyomiri-detail nyomiri-detail-skeleton" aria-busy="true">
      <button className="nyomiri-detail-back" style={{ visibility: 'hidden', pointerEvents: 'none' }} aria-hidden="true">
        Atrás
      </button>

      {/* Hero section skeleton */}
      <section className="nyomiri-detail-hero has-banner">
        <div className="nyomiri-detail-backdrop">
          <Skeleton
            variant="rectangular"
            animation="wave"
            sx={{ width: '100%', height: '100%', bgcolor: 'rgba(255, 255, 255, 0.04)' }}
          />
        </div>

        <div className="nyomiri-detail-intro">
          {/* Cover skeleton: 2:3 aspect ratio */}
          <Skeleton
            variant="rounded"
            animation="wave"
            className="nyomiri-detail-cover"
            sx={{
              width: { xs: 120, md: 192 },
              height: { xs: 180, md: 288 },
              aspectRatio: '2/3',
              borderRadius: '12px',
              bgcolor: 'rgba(255, 255, 255, 0.08)',
              flexShrink: 0,
            }}
          />

          {/* Metadata & Synopsis summary skeleton */}
          <div className="nyomiri-detail-summary" style={{ flex: 1, minWidth: 0 }}>
            {/* Title */}
            <Skeleton
              variant="text"
              animation="wave"
              sx={{ width: '75%', height: { xs: 32, md: 44 }, bgcolor: 'rgba(255, 255, 255, 0.1)' }}
            />
            {/* Alias */}
            <Skeleton
              variant="text"
              animation="wave"
              sx={{ width: '45%', height: 20, mt: 0.5, bgcolor: 'rgba(255, 255, 255, 0.06)' }}
            />
            {/* Meta */}
            <Skeleton
              variant="text"
              animation="wave"
              sx={{ width: '55%', height: 20, mt: 0.5, bgcolor: 'rgba(255, 255, 255, 0.06)' }}
            />
            {/* Synopsis lines */}
            <Box sx={{ mt: 1.5, mb: 2 }}>
              <Skeleton
                variant="text"
                animation="wave"
                sx={{ width: '100%', height: 18, bgcolor: 'rgba(255, 255, 255, 0.06)' }}
              />
              <Skeleton
                variant="text"
                animation="wave"
                sx={{ width: '92%', height: 18, bgcolor: 'rgba(255, 255, 255, 0.06)' }}
              />
              <Skeleton
                variant="text"
                animation="wave"
                sx={{ width: '70%', height: 18, bgcolor: 'rgba(255, 255, 255, 0.06)' }}
              />
            </Box>
            {/* Action buttons */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
              <Skeleton
                variant="rounded"
                animation="wave"
                sx={{ width: 140, height: 38, borderRadius: '8px', bgcolor: 'rgba(255, 255, 255, 0.1)' }}
              />
              <Skeleton
                variant="rounded"
                animation="wave"
                sx={{ width: 150, height: 38, borderRadius: '8px', bgcolor: 'rgba(255, 255, 255, 0.07)' }}
              />
              <Skeleton
                variant="rounded"
                animation="wave"
                sx={{ width: 180, height: 38, borderRadius: '8px', bgcolor: 'rgba(255, 255, 255, 0.07)' }}
              />
            </Box>
          </div>
        </div>
      </section>

      {/* Chapters / Episodes skeleton rows */}
      <div className="nyomiri-detail-columns">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Skeleton
            variant="text"
            animation="wave"
            sx={{ width: 160, height: 28, mb: 2, bgcolor: 'rgba(255, 255, 255, 0.08)' }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton
                key={i}
                variant="rounded"
                animation="wave"
                sx={{
                  width: '100%',
                  height: 48,
                  borderRadius: '8px',
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                }}
              />
            ))}
          </Box>
        </Box>
      </div>
    </article>
  );
}
